import { fromJS, List } from "immutable";
import Comment from "~/records/record.comment.jsx";
import CommentMap from "~/records/record.comment_map.jsx";
import * as actionTypes from "~/store/action_types";

export const comment = (state = new Comment(), action) => {
  switch (action.type) {
    case actionTypes.ADD_COMMENT_LOADING: {
      return new Comment({
        id: action.commentId,
        body: action.attributes.body,
        body_formatted: action.attributes.body,
        parent_type: action.parentType,
        parent_id: action.parentId,
        created_by_id: action.attributes.created_by_id,
        created_on: action.attributes.created_on,
        pending: true,
        attach_uploaded_files: List(action.attributes.attach_uploaded_files),
        drop_attached_files: List(action.attributes.drop_attached_files),
      });
    }
    case actionTypes.EDIT_COMMENT_LOADING:
      return new Comment({
        ...state.toObject(),
        body: action.attributes.body,
        body_formatted: action.attributes.body,
        pending: true,
        attach_uploaded_files: List(action.attributes.attach_uploaded_files),
        drop_attached_files: List(action.attributes.drop_attached_files),
        _error: undefined,
      });
    case actionTypes.ADD_COMMENT_ERROR:
    case actionTypes.EDIT_COMMENT_ERROR:
      return new Comment({
        ...state.toObject(),
        _error: action.error,
      });
    case actionTypes.MOVE_COMMENT_TO_TRASH_LOADING:
      return new Comment({
        ...state.toObject(),
        pending: true,
      });
    case actionTypes.MOVE_COMMENT_TO_TRASH_SUCCESS:
      return new Comment({
        ...state.toObject(),
        is_trashed: true,
        pending: false,
      });
    case actionTypes.MOVE_COMMENT_TO_TRASH_ERROR:
      return new Comment({
        ...state.toObject(),
        pending: false,
      });
    case actionTypes.RETRY_SUBMIT_COMMENT_LOADING:
      return new Comment({
        ...state.toObject(),
        pending: true,
        _error: undefined,
      });
    case actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_SUCCESS: {
      return new Comment(
        fromJS({
          ...action.response.entities.comments[action.id],
          is_real_time: true,
        })
      );
    }
    case actionTypes.REMOVE_REACTION_LOADING:
    case actionTypes.REMOVE_REACTION_SUCCESS: {
      if (action.reactionId) {
        let index = state.get(`reactions`).findIndex((reaction) => reaction === action.reactionId);
        let reactions = state.get(`reactions`).delete(`${index}`);

        return new Comment({ ...state.toObject(), reactions });
      }
      return state;
    }
    case actionTypes.REMOVE_REACTION_ERROR: {
      let reactions = state.get(`reactions`).push(action.reactionId);
      return new Comment({ ...state.toObject(), reactions });
    }
    case actionTypes.ADD_REACTION_LOADING: {
      let reactions = state.get(`reactions`).push(action.id);
      return new Comment({ ...state.toObject(), reactions });
    }
    case actionTypes.ADD_REACTION_SUCCESS:
      if (action.response && action.response.result && action.response.result.single) {
        let reactions = state.get(`reactions`);
        reactions = reactions.filter((reaction) => reaction !== action.id);

        if (reactions.indexOf(action.response.result.single) < 0) {
          reactions = reactions.push(action.response.result.single);
        }

        return new Comment({ ...state.toObject(), reactions });
      }
      return state;
    case actionTypes.ADD_REACTION_ERROR: {
      let reactions = state.get(`reactions`).filter((reaction) => reaction !== action.id);

      return new Comment({ ...state.toObject(), reactions });
    }
    case actionTypes.ADD_REACTION_FROM_EVENT_STREAM_SUCCESS: {
      if (action.response && action.response.result) {
        let reactions = state.get(`reactions`);

        if (reactions.indexOf(action.response.result) > -1) {
          return state;
        }

        reactions = reactions.push(action.response.result);

        return new Comment({ ...state.toObject(), reactions });
      }
      return state;
    }
    case actionTypes.REMOVE_REACTION_FROM_EVENT_STREAM_SUCCESS: {
      if (action.response && action.response.result) {
        let reactions = state.get(`reactions`).filter((reactionId) => reactionId !== action.response.result);

        return new Comment({ ...state.toObject(), reactions });
      }
      return state;
    }
    case actionTypes.UPDATE_COMMENT_FROM_EVENT_STREAM_SUCCESS: {
      const { response = {} } = action;
      const { entities = {}, result: commentId = 0 } = response;
      const { comments = {} } = entities;
      const comment = comments[commentId];
      if (comment) {
        return new Comment(
          fromJS({
            ...state.toObject(),
            ...comment,
          })
        );
      }
      return state;
    }
    default:
      return state;
  }
};

const mergeEntities = (state, newComments) => {
  return state.merge(newComments.map((c) => new Comment(c)));
};

const comments = (state = new CommentMap(), action) => {
  switch (action.type) {
    case actionTypes.ADD_COMMENT_LOADING:
    case actionTypes.ADD_COMMENT_ERROR:
    case actionTypes.EDIT_COMMENT_LOADING:
    case actionTypes.EDIT_COMMENT_ERROR:
    case actionTypes.RETRY_SUBMIT_COMMENT_LOADING:
      return state.set(action.commentId.toString(), comment(state.get(action.commentId.toString(), undefined), action));
    case actionTypes.ADD_COMMENT_SUCCESS:
      return mergeEntities(state.remove(action.commentId.toString()), fromJS(action.response.entities.comments));
    case actionTypes.FETCH_DISCUSSIONS_SUCCESS:
    case actionTypes.EDIT_COMMENT_SUCCESS:
    case actionTypes.FETCH_COMMENTS_SUCCESS:
    case actionTypes.FETCH_ACTIVITY_LOGS_SUCCESS:
    case actionTypes.FETCH_NOTE_SUCCESS:
    case actionTypes.FETCH_DISCUSSION_SUCCESS:
    case actionTypes.PREFETCH_DISCUSSION_SUCCESS:
    case actionTypes.LOAD_TASK:
    case actionTypes.FETCH_ATTACHMENTS_SUCCESS:
    case actionTypes.FETCH_FILES_AND_ATTACHMENTS_SUCCESS:
    case actionTypes.FETCH_CALENDAR_EVENT_SUCCESS:
    case actionTypes.ANGULAR_INIT_TASK_AND_COMMENTS_MODAL:
      if (!action.response || !action.response.entities || !action.response.entities.comments) {
        return state;
      }
      return mergeEntities(state, fromJS(action.response.entities.comments));
    case actionTypes.MOVE_COMMENT_TO_TRASH_LOADING:
    case actionTypes.MOVE_COMMENT_TO_TRASH_SUCCESS:
    case actionTypes.MOVE_COMMENT_TO_TRASH_ERROR:
      if (!action.id || action.objectType !== `Comment` || !state.get(action.id.toString())) {
        return state;
      }
      return state.set(`${action.id}`, comment(state.get(`${action.id}`, undefined), action));
    case actionTypes.UPDATE_COMMENT_FROM_EVENT_STREAM_SUCCESS: {
      if (action?.id > 0) {
        return state.set(`${action.id}`, comment(state.get(`${action.id}`, undefined), action));
      }
      return state;
    }
    case actionTypes.ADD_COMMENT_FROM_EVENT_STREAM_SUCCESS:
      if (!state.get(`${action.id}`)) {
        return state.set(`${action.id}`, comment(new Comment(), action));
      }
      return state;
    case actionTypes.REMOVE_REACTION_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.ADD_REACTION_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.ADD_REACTION_LOADING:
    case actionTypes.ADD_REACTION_SUCCESS:
    case actionTypes.ADD_REACTION_ERROR:
    case actionTypes.REMOVE_REACTION_LOADING:
    case actionTypes.REMOVE_REACTION_ERROR:
    case actionTypes.REMOVE_REACTION_SUCCESS: {
      const { parentId = undefined, parentType = undefined } = action;
      if (parentId && parentType === `Comment`) {
        return state.set(`${parentId}`, comment(state.get(`${parentId}`), action));
      }
      return state;
    }
    default:
      return state;
  }
};

export default comments;
